/**
 * Admin API layer for Slide Sense management.
 *
 * Uses the shared Axios client from @/lib/api-client which handles
 * authentication, base URL, and response unwrapping.
 */

import type {
  BulkImportJobResponse,
  BulkImportManifestFile,
  BulkImportPreflight,
  FolderDeletePreview,
  SlideSenseCollection,
  SlideSenseDeck,
  SlideSenseFolder,
  SlideSenseSlide,
  UsageNode,
  UsageSummary,
  UsageTreeMap,
  UsageWindow,
} from "./types"

import { api } from "@/lib/api-client"

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export interface CreateFolderRequest {
  name: string
  description?: string
  parent_folder_id?: string | null
  sort_order?: number
}

export interface UpdateFolderRequest {
  name?: string
  description?: string
  parent_folder_id?: string | null
  sort_order?: number
}

export interface DeckStatusResponse {
  id: string
  processing_status: SlideSenseDeck["processing_status"]
  processing_progress: number
  processing_error: string | null
  slide_count: number
}

export interface BulkImportRequest {
  target_folder_id?: string | null
  root_folder_name: string
  files: BulkImportManifestFile[]
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * QUERY: Fetch all folders (admin endpoint).
 */
export function fetchAdminFolders(): Promise<SlideSenseFolder[]> {
  return api.get("/slide-sense/admin/folders")
}

/**
 * QUERY: Probe the caller's SlideSense admin role flags.
 * `is_admin` is always true on success (the endpoint is admin-gated).
 * `is_super_admin` indicates membership in the `slide_sense_super_admins`
 * group, which currently gates bulk import.
 */
export function fetchSlideSenseAdminMe(): Promise<{
  is_admin: boolean
  is_super_admin: boolean
}> {
  return api.get("/slide-sense/admin/me")
}

/**
 * MUTATION: Create a new folder.
 */
export function createFolder(data: CreateFolderRequest): Promise<SlideSenseFolder> {
  return api.post("/slide-sense/admin/folders", data)
}

/**
 * MUTATION: Update an existing folder.
 */
export function updateFolder(id: string, data: UpdateFolderRequest): Promise<SlideSenseFolder> {
  return api.put(`/slide-sense/admin/folders/${id}`, data)
}

/**
 * MUTATION: Delete a folder.
 */
export function deleteFolder(id: string): Promise<{
  deleted: boolean
  decks_deleted: number
  slides_deleted: number
  folders_deleted: number
}> {
  return api.delete(`/slide-sense/admin/folders/${id}`)
}

/**
 * QUERY: Cascade-delete impact preview for a folder.
 */
export function getFolderDeletePreview(id: string): Promise<FolderDeletePreview> {
  return api.get(`/slide-sense/admin/folders/${id}/delete-preview`)
}

/**
 * QUERY: Classify a folder manifest before uploading files.
 */
export function preflightBulkImport(data: BulkImportRequest): Promise<BulkImportPreflight> {
  return api.post("/slide-sense/admin/bulk-imports/preflight", data)
}

/**
 * MUTATION: Create a durable bulk import job.
 */
export function createBulkImport(data: BulkImportRequest): Promise<BulkImportJobResponse> {
  return api.post("/slide-sense/admin/bulk-imports", data)
}

/**
 * QUERY: Fetch a bulk import job and its file items.
 */
export function fetchBulkImport(jobId: string): Promise<BulkImportJobResponse> {
  return api.get(`/slide-sense/admin/bulk-imports/${jobId}`)
}

/**
 * MUTATION: Upload one new/changed file for a bulk import job.
 */
export async function uploadBulkImportFile(
  jobId: string,
  file: File,
  relativePath: string
): Promise<{ id: string; status: string }> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("relative_path", relativePath)

  return api.post(`/slide-sense/admin/bulk-imports/${jobId}/files`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

/**
 * MUTATION: Upload a .pptx deck file to a folder.
 *
 * Sends a multipart/form-data request with the file and folder ID.
 */
export async function uploadDeck(file: File, folderId: string): Promise<SlideSenseDeck> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("folder_id", folderId)

  return api.post("/slide-sense/admin/decks/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

/**
 * Backend shape returned when /admin/decks/upload short-circuits on a
 * content-hash duplicate (HTTP 409). See ``admin_upload_deck`` in
 * ``routes/slide_sense.py``.
 */
export interface UploadDuplicateInfo {
  duplicateOf: string
  existingFilename?: string
  existingStatus?: string
}

/**
 * Inspect an upload error and return duplicate metadata if the backend
 * responded with 409 + ``error: "duplicate"``. Returns ``null`` for any
 * other failure shape so callers can fall back to a generic toast.
 *
 * Lives alongside :func:`uploadDeck` so the duplicate contract stays
 * co-located with the request that produces it.
 */
export function getUploadDuplicateInfo(err: unknown): UploadDuplicateInfo | null {
  if (typeof err !== "object" || err === null) return null
  // Axios shape: error.response.{status,data}
  const response = (err as { response?: { status?: number; data?: unknown } }).response
  if (!response || response.status !== 409) return null
  const data = response.data
  if (typeof data !== "object" || data === null) return null
  const body = data as {
    error?: string
    duplicate_of?: unknown
    duplicate_filename?: unknown
    duplicate_status?: unknown
  }
  if (body.error !== "duplicate" || typeof body.duplicate_of !== "string") return null
  return {
    duplicateOf: body.duplicate_of,
    existingFilename:
      typeof body.duplicate_filename === "string" ? body.duplicate_filename : undefined,
    existingStatus: typeof body.duplicate_status === "string" ? body.duplicate_status : undefined,
  }
}

/**
 * QUERY: Fetch the processing status of a deck.
 */
export function fetchDeckStatus(deckId: string): Promise<DeckStatusResponse> {
  return api.get(`/slide-sense/admin/decks/${deckId}/status`)
}

/**
 * QUERY: Fetch all decks, optionally filtered by folder.
 */
export function fetchAdminDecks(folderId?: string): Promise<SlideSenseDeck[]> {
  const params = folderId ? { folder_id: folderId } : undefined
  return api.get("/slide-sense/admin/decks", { params })
}

/**
 * QUERY: Fetch cover slide IDs for all decks (deck_id -> first_slide_id).
 */
export function fetchDeckCovers(): Promise<Record<string, string>> {
  return api.get("/slide-sense/admin/decks/covers")
}

/**
 * MUTATION: Rename a deck or move it to a different folder.
 */
export function updateDeck(
  deckId: string,
  data: { original_filename?: string; folder_id?: string; sort_order?: number }
): Promise<SlideSenseDeck> {
  return api.put(`/slide-sense/admin/decks/${deckId}`, data)
}

/**
 * MUTATION: Delete a deck and its associated slides.
 */
export function deleteDeck(deckId: string): Promise<{ deleted: boolean; slides_deleted: number }> {
  return api.delete(`/slide-sense/admin/decks/${deckId}`)
}

/**
 * MUTATION: Re-process a deck. Used after a `failed` or
 * `completed_with_errors` outcome, or when the renderer / orchestrator
 * had a transient outage. Re-downloads the original PPTX from blob,
 * deletes any partial slide rows, and re-runs the pipeline. The deck
 * keeps its id, original_filename, and folder_id — slides get new ids.
 */
export function reprocessDeck(deckId: string): Promise<{ id: string; processing_status: string }> {
  return api.post(`/slide-sense/admin/decks/${deckId}/reprocess`, {})
}

/**
 * MUTATION: Delete a single slide.
 */
export function deleteSlide(slideId: string): Promise<{ deleted: boolean }> {
  return api.delete(`/slide-sense/admin/slides/${slideId}`)
}

/**
 * Fill in defaults for AI-metadata fields the type marks as required but
 * Cosmos may have stored as absent — legacy slides predating the
 * extraction pipeline can have any of these missing. Done at the boundary
 * so callers can trust the shape they get back.
 */
function normalizeAdminSlide(raw: unknown): SlideSenseSlide {
  const r = raw as Partial<SlideSenseSlide> & Record<string, unknown>
  return {
    ...(r as SlideSenseSlide),
    ai_title: typeof r.ai_title === "string" ? r.ai_title : "",
    ai_summary: typeof r.ai_summary === "string" ? r.ai_summary : "",
    ai_keywords: Array.isArray(r.ai_keywords) ? r.ai_keywords : [],
    text_content: typeof r.text_content === "string" ? r.text_content : "",
  }
}

/**
 * QUERY: Fetch a single slide by id (admin, tenant-scoped).
 * Returns the same SlideSenseSlide shape as the public list/search endpoints,
 * so it can be fed straight into SlidePreviewModal.
 */
export async function fetchAdminSlide(slideId: string): Promise<SlideSenseSlide> {
  const raw = await api.get(`/slide-sense/admin/slides/${slideId}`)
  return normalizeAdminSlide(raw)
}

/**
 * QUERY: Fetch a single collection by id with embedded slides (admin,
 * tenant-scoped). Used by the dead-presentations report to let admins peek
 * inside a collection before cleanup.
 */
export async function fetchAdminCollection(
  collectionId: string
): Promise<SlideSenseCollection & { slides: SlideSenseSlide[] }> {
  const raw = (await api.get(`/slide-sense/admin/collections/${collectionId}`)) as Omit<
    SlideSenseCollection,
    "slides"
  > & { slides?: unknown[] }
  const slides = Array.isArray(raw.slides) ? raw.slides.map(normalizeAdminSlide) : []
  return { ...raw, slides }
}

/** Inline-edit payload. Any combination of the four fields may be sent;
 *  the backend rejects unknown keys and applies what's present. */
export interface UpdateSlideMetadataRequest {
  ai_title?: string
  ai_summary?: string
  ai_keywords?: string[]
  category?: string
}

/**
 * MUTATION: Update editable AI metadata fields on a slide.
 */
export function updateSlideMetadata(
  slideId: string,
  data: UpdateSlideMetadataRequest
): Promise<SlideSenseSlide> {
  return api.put(`/slide-sense/admin/slides/${slideId}`, data)
}

// ---------------------------------------------------------------------------
// Usage / telemetry fetchers (Task 14)
// ---------------------------------------------------------------------------

/**
 * QUERY: Fetch aggregated usage KPIs, daily series, and top-N lists.
 */
export function fetchUsageSummary(window: UsageWindow = "30d"): Promise<UsageSummary> {
  return api.get(`/slide-sense/admin/usage/summary?window=${window}`)
}

/**
 * QUERY: Fetch a flat tree-map of usage counts keyed by folder/deck/slide ID.
 */
export function fetchUsageTree(window: UsageWindow = "30d"): Promise<UsageTreeMap> {
  return api.get(`/slide-sense/admin/usage/tree?window=${window}`)
}

/**
 * QUERY: Fetch usage detail for a single folder, deck, or slide node.
 */
export function fetchUsageNode(
  type: "folder" | "deck" | "slide",
  id: string,
  window: UsageWindow = "30d"
): Promise<UsageNode> {
  return api.get(
    `/slide-sense/admin/usage/node?type=${type}&id=${encodeURIComponent(id)}&window=${window}`
  )
}

export interface UsageUnusedSlide {
  slide_id: string
  ai_title: string | null
  category: string | null
  deck_id: string | null
  deck_name: string | null
  folder_id: string | null
  created_at: string | null
  updated_at: string | null
}

export interface UsageUnusedCollection {
  collection_id: string
  name: string | null
  owner_user_id: string | null
  owner_name: string | null
  slide_count: number
  /** First few member slide ids — lets the frontend render a thumbnail
   *  strip per row without an extra round-trip. */
  preview_slide_ids: string[]
  created_at: string | null
  updated_at: string | null
}

export interface UsageUnusedResponse {
  window_days: number
  event_ttl_days: number
  unused_slides: UsageUnusedSlide[]
  unused_slides_total: number
  unused_collections: UsageUnusedCollection[]
  unused_collections_total: number
  default_stale_window_days: number
  min_days: number
  max_days: number
}

/**
 * Boundary normalizer for /admin/usage/unused. Coalesces missing arrays
 * to [] and missing counts to 0, so consumers can rely on the
 * UsageUnusedResponse contract even if the backend hiccups or the cache
 * returns a stale-shape value.
 */
function normalizeUsageUnused(raw: unknown): UsageUnusedResponse {
  const r = (raw ?? {}) as Partial<UsageUnusedResponse> & Record<string, unknown>
  const slides = Array.isArray(r.unused_slides) ? r.unused_slides : []
  const collections = Array.isArray(r.unused_collections)
    ? r.unused_collections.map((c) => ({
        ...c,
        preview_slide_ids: Array.isArray(c.preview_slide_ids) ? c.preview_slide_ids : [],
      }))
    : []
  return {
    window_days: typeof r.window_days === "number" ? r.window_days : 0,
    event_ttl_days: typeof r.event_ttl_days === "number" ? r.event_ttl_days : 180,
    unused_slides: slides,
    unused_slides_total:
      typeof r.unused_slides_total === "number" ? r.unused_slides_total : slides.length,
    unused_collections: collections,
    unused_collections_total:
      typeof r.unused_collections_total === "number"
        ? r.unused_collections_total
        : collections.length,
    default_stale_window_days:
      typeof r.default_stale_window_days === "number" ? r.default_stale_window_days : 90,
    min_days: typeof r.min_days === "number" ? r.min_days : 1,
    max_days: typeof r.max_days === "number" ? r.max_days : 365,
  }
}

/**
 * QUERY: Slides + collections with no activity in the last N days.
 * Pass ``days`` to override the route-configured default.
 */
export async function fetchUsageUnused(days?: number): Promise<UsageUnusedResponse> {
  const qs = days !== undefined ? `?days=${days}` : ""
  const raw = await api.get(`/slide-sense/admin/usage/unused${qs}`)
  return normalizeUsageUnused(raw)
}

/**
 * MUTATION: Delete a saved presentation as admin (tenant-scoped,
 * regardless of owner). Used by the dead-presentations cleanup flow.
 */
export function adminDeleteCollection(collectionId: string): Promise<{ deleted: boolean }> {
  return api.delete(`/slide-sense/admin/collections/${collectionId}`)
}
